/**
 * CRM Chat Inbox (Iter 90) — /crm/chats
 * -------------------------------------
 * Left: conversation list, filterable by status (all / pending / human).
 * Right: active conversation with polling + composer + close + export.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { StartChatButton } from "../../components/crm/StartChatButton";
import { LiveSuggestion, TranslateDraftButton, TranslateMessage } from "../../components/crm/ChatAssist";
import { api, API_BASE, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  ChatCircle, PaperPlaneRight, User, Circle, XCircle,
  DownloadSimple, Users, MagnifyingGlass, FileCsv,
  Plus, Ticket, UserCirclePlus, ArrowsClockwise, CheckCircle,
  Phone, WhatsappLogo, EnvelopeSimple, Smiley, Truck,
  SmileyMeh, SmileyAngry, SmileyXEyes, Copy, ChatText,
  CaretUp, CaretDown, Sparkle, Robot,
} from "@phosphor-icons/react";
import { RigoAvatar } from "../../components/rigo";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import { TicketFormModal, emptyForm } from "./Tickets";
import { linkify } from "../../lib/linkify";
import { substituteTokens } from "../../lib/templateTokens";
import { TemplatePicker } from "../../components/TemplatePicker";
import { MagicRewrite } from "../../components/MagicRewrite";

const POLL_MS = 2500;   // Iter 138 — faster live-refresh (was 5000)
const STATUS_TABS = [
  { key: "",        label: "All" },
  { key: "pending", label: "Pending" },
  { key: "human",   label: "Handling" },
  { key: "bot",     label: "Bot" },
  { key: "closed",  label: "Closed" },
];

// Iter 216 — Quick filters (orthogonal to STATUS_TABS above)
const QUICK_FILTERS = [
  { key: "new",    label: "New" },       // last 24 h
  { key: "unread", label: "Unread" },    // unread_count_staff > 0
  { key: "active", label: "Active" },    // status != closed / bot
];

export default function Chats() {
  const [startParams, setStartParams] = useSearchParams();
  const startCustomerId = startParams.get("start");
  const [conversations, setConversations] = useState([]);
  const [status, setStatus] = useState("");
  // Iter 216 — client-side quick filters + sort. Orthogonal to the
  // status tabs (a "new" chat is always also "pending"; "unread" +
  // "human" + "pending" all overlap). Kept in local state so a
  // browser refresh returns to the default "all + latest".
  const [quickFilter, setQuickFilter] = useState("");   // "" | "new" | "unread" | "active"
  const [sortMode, setSortMode]       = useState("latest"); // "latest" | "unread"
  const [q, setQ] = useState("");
  const [activeId, setActiveId] = useState(null);
  const [stats, setStats] = useState(null);
  const [showBulkExport, setShowBulkExport] = useState(false);
  // Iter 157 — tenant-wide AI switch (superadmin only can flip).
  const [aiGlobalEnabled, setAiGlobalEnabled] = useState(true);
  const [aiToggleBusy, setAiToggleBusy] = useState(false);
  const { user: authUser } = useAuth();
  const isSuperadmin = authUser?.role === "superadmin";
  // Iter 138 — Up/Down scroll buttons for the conv list.
  const convListRef = useRef(null);
  const scrollConvs = (dir) => {
    const el = convListRef.current;
    if (!el) return;
    const step = Math.max(120, Math.floor(el.clientHeight * 0.7));
    el.scrollBy({ top: dir === "up" ? -step : step, behavior: "smooth" });
  };

  const loadConvs = useCallback(async () => {
    const url = status
      ? `/chat/conversations?group_by_phone=true&status=${status}`
      : "/chat/conversations?group_by_phone=true";
    const [c, s] = await Promise.all([api.get(url), api.get("/chat/stats")]);
    setConversations(c.data); setStats(s.data);
  }, [status]);
  useEffect(() => { loadConvs(); }, [loadConvs]);
  useEffect(() => {
    const t = setInterval(loadConvs, POLL_MS);
    return () => clearInterval(t);
  }, [loadConvs]);

  // Iter 157 — load & poll tenant-wide AI switch.
  useEffect(() => {
    let alive = true;
    const load = () => api.get("/crm/settings/ai-chat")
      .then(r => { if (alive) setAiGlobalEnabled(!!r.data?.enabled); })
      .catch(() => {});
    load();
    const t = setInterval(load, 15000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const flipGlobalAi = async () => {
    if (aiToggleBusy) return;
    setAiToggleBusy(true);
    try {
      const next = !aiGlobalEnabled;
      const r = await api.put("/crm/settings/ai-chat", { enabled: next });
      setAiGlobalEnabled(!!r.data?.enabled);
      toast.success(`AI chat ${next ? "ENABLED" : "DISABLED"} tenant-wide`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAiToggleBusy(false); }
  };

  // Iter 216 — apply search + quick-filter + sort in one memo so the
  // list is stable across polling refreshes. Quick-filter buckets:
  //   · new    — created in the last 24 h
  //   · unread — unread_count_staff > 0
  //   · active — status ∈ {pending, human, open} (i.e. not closed/bot)
  const filtered = useMemo(() => {
    const now = Date.now();
    const H24 = 24 * 60 * 60 * 1000;
    const kw = q.trim().toLowerCase();
    let rows = conversations.filter(c => {
      if (kw && !`${c.customer_name} ${c.customer_phone} ${c.last_message_preview}`
          .toLowerCase().includes(kw)) return false;
      if (quickFilter === "new") {
        const t = new Date(c.created_at || 0).getTime();
        if (isNaN(t) || (now - t) > H24) return false;
      } else if (quickFilter === "unread") {
        if (!(Number(c.unread_count_staff) > 0)) return false;
      } else if (quickFilter === "active") {
        const s = String(c.status || "").toLowerCase();
        if (!(s === "pending" || s === "human" || s === "open")) return false;
      }
      return true;
    });
    if (sortMode === "unread") {
      rows = [...rows].sort((a, b) => {
        const ua = Number(a.unread_count_staff || 0);
        const ub = Number(b.unread_count_staff || 0);
        if (ub !== ua) return ub - ua;
        // Tiebreak on latest.
        return new Date(b.updated_at || 0) - new Date(a.updated_at || 0);
      });
    } else {
      rows = [...rows].sort((a, b) =>
        new Date(b.updated_at || 0) - new Date(a.updated_at || 0));
    }
    return rows;
  }, [conversations, q, quickFilter, sortMode]);

  return (
    <AppShell title="Chats" subtitle="Module 4 · CRM · Customer conversations">
      {/* Toolbar */}
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className="text-[11px] uppercase tracking-[0.15em] text-[#8A8780] inline-flex items-center gap-2">
          <span className="inline-block w-2 h-2 rounded-full bg-[#2D5A27] chat-live-dot"
                title="Chats auto-refresh every 2.5 seconds" data-testid="chat-live-dot"/>
          <span>Live</span>
          <span className="opacity-50">·</span>
          {conversations.length} conversation{conversations.length === 1 ? "" : "s"}
          {filtered.length !== conversations.length && ` · ${filtered.length} shown`}
        </div>
        <div className="flex items-center gap-2">
          <StartChatButton initialCustomerId={startCustomerId}
                           onClearInitial={() => setStartParams({}, { replace: true })}
                           onStarted={(id) => { loadConvs(); setActiveId(id); }} />
          {/* Global AI badge — visible to every staff so they know the current state */}
          <span data-testid="chat-global-ai-badge"
                title={aiGlobalEnabled
                  ? "AI chat is enabled for this tenant"
                  : "AI chat is disabled tenant-wide by superadmin"}
                className={`inline-flex items-center gap-1 h-9 px-2.5 rounded-md text-[11px] font-semibold uppercase tracking-[0.14em] border ${
                  aiGlobalEnabled
                    ? "bg-[#EFF5EC] border-[#B5D5AC] text-[#1A3317]"
                    : "bg-[#F0EFEA] border-[#E5E2D9] text-[#8A8780]"
                }`}>
            <Robot size={12} weight={aiGlobalEnabled ? "fill" : "regular"}/>
            AI · {aiGlobalEnabled ? "ON" : "OFF"}
          </span>
          {isSuperadmin && (
            <button onClick={flipGlobalAi} disabled={aiToggleBusy}
                    data-testid="chat-global-ai-toggle"
                    title={aiGlobalEnabled
                      ? "Superadmin · Turn AI chat OFF for every staff"
                      : "Superadmin · Turn AI chat ON for every staff"}
                    className={`h-9 px-3 rounded-md text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-60 ${
                      aiGlobalEnabled
                        ? "bg-white border border-[#7A2E18] text-[#7A2E18] hover:bg-[#FBEBE5]"
                        : "bg-[#2D5A27] hover:bg-[#3A7033] text-white"
                    }`}>
              {aiGlobalEnabled ? "Turn AI OFF" : "Turn AI ON"}
            </button>
          )}
          <button onClick={() => setShowBulkExport(true)}
                  data-testid="chat-bulk-export-btn"
                  className="h-9 px-3 rounded-md bg-[#2D5A27] hover:bg-[#3A7033] text-white text-xs font-semibold inline-flex items-center gap-1.5">
            <DownloadSimple size={13} weight="fill"/>
            Download all chats
          </button>
        </div>
      </div>

      {/* Stats strip */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
          <Kpi label="Total" value={stats.total_conversations}/>
          <Kpi label="Pending" value={stats.pending} color="text-[#7A2E18]"/>
          <Kpi label="With staff" value={stats.human} color="text-[#1F3A57]"/>
          <Kpi label="Msgs 24h" value={stats.messages_24h}/>
          <Kpi label="Msgs 7d" value={stats.messages_7d}/>
        </div>
      )}

      <div className="grid grid-cols-12 gap-4 bg-white border border-[#E5E2D9] rounded-md overflow-hidden"
           style={{ height: "calc(100vh - 200px)", minHeight: 560 }}>
        {/* Left pane — search+filters frozen at top, list scrolls below */}
        <div className="col-span-4 border-r border-[#E5E2D9] flex flex-col min-h-0">
          <div className="p-3 border-b border-[#E5E2D9] space-y-2 shrink-0 sticky top-0 z-10 bg-white"
               data-testid="chat-list-header">
            <div className="relative">
              <MagnifyingGlass size={13} className="absolute left-2.5 top-2.5 text-[#8A8780]"/>
              <input value={q} onChange={e => setQ(e.target.value)}
                     placeholder="Search customer / phone"
                     data-testid="chat-search"
                     className="w-full h-9 pl-8 pr-3 border border-[#E5E2D9] rounded-md text-sm"/>
            </div>
            <div className="flex gap-1 overflow-x-auto">
              {STATUS_TABS.map(t => (
                <button key={t.key} onClick={() => setStatus(t.key)}
                        data-testid={`chat-tab-${t.key || "all"}`}
                        className={`px-2 h-7 rounded text-[11px] uppercase tracking-[0.14em] font-semibold whitespace-nowrap ${
                          status === t.key
                            ? "bg-[#2D5A27] text-white"
                            : "bg-[#F7F6F2] text-[#5C5A55] hover:bg-[#EEEBE2]"
                        }`}>{t.label}</button>
              ))}
            </div>
            {/* Iter 216 — Quick filters + sort */}
            <div className="flex items-center gap-1 overflow-x-auto"
                  data-testid="chat-quick-filter-row">
              {QUICK_FILTERS.map(f => {
                const on = quickFilter === f.key;
                return (
                  <button key={f.key || "any"} type="button"
                           onClick={() => setQuickFilter(on ? "" : f.key)}
                           data-testid={`chat-qf-${f.key || "all"}`}
                           className={`inline-flex items-center gap-1 px-2 h-7 rounded-full text-[11px] font-medium whitespace-nowrap border ${
                             on
                               ? "bg-[#DFEEDA] text-[#1F3A19] border-[#B7D3AF]"
                               : "bg-white text-[#5C5A55] border-[#E5E2D9] hover:bg-[#F7F6F2]"
                           }`}>
                    {f.label}
                  </button>
                );
              })}
              <div className="flex-1"/>
              <label className="text-[10px] uppercase tracking-[0.14em] text-[#8A8780] shrink-0">
                Sort
              </label>
              <select value={sortMode} onChange={e => setSortMode(e.target.value)}
                       data-testid="chat-sort-select"
                       className="h-7 px-1.5 border border-[#E5E2D9] rounded text-[11px] bg-white shrink-0">
                <option value="latest">Latest</option>
                <option value="unread">Unread first</option>
              </select>
            </div>
            <a href="/settings/chat-bot" className="text-[11px] text-[#2D5A27] underline block">
              Configure Rigo (bot triggers, hours, retention)
            </a>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto crm-chat-list relative"
               data-testid="chat-conv-list"
               ref={convListRef}>
            {filtered.length === 0 && (
              <div className="text-center text-[#8A8780] text-sm py-10">
                <ChatCircle size={24} className="inline-block mb-2 opacity-40"/><br/>No conversations yet.
              </div>
            )}
            {filtered.map(c => (
              <button key={c.id} onClick={() => setActiveId(c.id)}
                      data-testid={`chat-conv-${c.id}`}
                      className={`w-full text-left px-3 py-3 border-b border-[#F0EEE6] hover:bg-[#F7F6F2] ${
                        activeId === c.id ? "bg-[#F0F7EE]" : ""
                      }`}>
                <div className="flex items-center gap-2">
                  <StatusDot status={c.status}/>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate flex items-center gap-1.5">
                      {c.customer_name || "—"}
                      <VipBadge tier={c.customer_priority_tier}
                                testid={`chat-conv-vip-${c.id}`}/>
                      {c.presence?.active && (
                        <span data-testid={`chat-presence-${c.id}`}
                              title={`On the customer app${c.presence.page_label ? " · " + c.presence.page_label : ""}`}
                              className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.14em] font-semibold text-white bg-[#2D5A27] px-1.5 py-0.5 rounded-full">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#B7E3AC] chat-live-dot"/>
                          Active
                        </span>
                      )}
                      {c.history?.length > 0 && (
                        <span title={`${c.history.length} past conversation(s) from this phone`}
                              data-testid={`chat-history-count-${c.id}`}
                              className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-[0.1em] font-semibold text-[#5C5A55] bg-[#EEEBE2] px-1.5 py-0.5 rounded-full">
                          +{c.history.length} past
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-[#8A8780] truncate">
                      {c.last_message_preview || "…"}
                    </div>
                    {c.presence?.active && c.presence.page_label && (
                      <div className="text-[10px] text-[#2D5A27] truncate mt-0.5"
                           data-testid={`chat-presence-page-${c.id}`}>
                        Viewing: <span className="font-medium">{c.presence.page_label}</span>
                      </div>
                    )}
                  </div>
                  {c.unread_count_staff > 0 && (
                    <span className="bg-[#7A2E18] text-white text-[10px] px-1.5 py-0.5 rounded-full font-bold">
                      {c.unread_count_staff}
                    </span>
                  )}
                </div>
                <div className="mt-1 text-[10px] text-[#8A8780]">
                  {c.customer_phone} · {new Date(c.last_message_at).toLocaleString()}
                </div>
              </button>
            ))}
          </div>
          {filtered.length > 6 && (
            <div className="border-t border-[#E5E2D9] px-2 py-1 flex justify-between items-center bg-[#FAFAF7] shrink-0"
                 data-testid="chat-conv-scroll-controls">
              <button type="button" onClick={() => scrollConvs("up")}
                      title="Scroll conversation list up"
                      data-testid="chat-conv-scroll-up"
                      className="h-7 px-2 rounded text-[#5C5A55] hover:bg-white inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.14em]">
                <CaretUp size={11} weight="bold"/>Up
              </button>
              <div className="text-[10px] text-[#8A8780] font-mono">
                {filtered.length} chats
              </div>
              <button type="button" onClick={() => scrollConvs("down")}
                      title="Scroll conversation list down"
                      data-testid="chat-conv-scroll-down"
                      className="h-7 px-2 rounded text-[#5C5A55] hover:bg-white inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.14em]">
                <CaretDown size={11} weight="bold"/>Down
              </button>
            </div>
          )}
        </div>

        {/* Right pane */}
        <div className="col-span-8 flex flex-col h-full min-h-0">
          {activeId
            ? <ChatPane cid={activeId} aiGlobalEnabled={aiGlobalEnabled}
                        onClose={() => { setActiveId(null); loadConvs(); }} onDirty={loadConvs}/>
            : (
              <div className="flex-1 flex flex-col items-center justify-center text-center text-[#8A8780]">
                <Users size={28} weight="regular" className="mb-3 opacity-40"/>
                <div className="text-sm">Pick a conversation on the left to jump in.</div>
                <a href={`${API_BASE}/chat/report?format=csv`} target="_blank" rel="noreferrer"
                    className="mt-4 text-xs text-[#2D5A27] underline inline-flex items-center gap-1">
                  <FileCsv size={12}/>Download full chat report (CSV)
                </a>
              </div>
            )}
        </div>
      </div>

      {showBulkExport && (
        <BulkExportModal conversations={conversations}
                          onClose={() => setShowBulkExport(false)}/>
      )}
    </AppShell>
  );
}

function Kpi({ label, value, color = "text-[#1A1A1A]" }) {
  return (
    <div className="bg-white border border-[#E5E2D9] rounded-md p-3">
      <div className="text-[10px] uppercase tracking-[0.18em] text-[#8A8780]">{label}</div>
      <div className={`font-heading text-2xl tabular-nums ${color}`}>{value}</div>
    </div>
  );
}
function StatusDot({ status }) {
  const map = {
    pending: "text-[#7A2E18]",
    human:   "text-[#1F3A57]",
    bot:     "text-[#2D5A27]",
    closed:  "text-[#8A8780]",
  };
  return <Circle size={9} weight="fill" className={map[status] || "text-[#8A8780]"}/>;
}

// Iter 157 — VIP/VVIP priority-tier badge (icon + pill) used in the
// staff chat inbox and chat header. Regular tier renders nothing so
// the UI stays clean for the 95% of normal customers.
function VipBadge({ tier, size = "sm", testid }) {
  const t = (tier || "regular").toLowerCase();
  if (t === "regular") return null;
  const cfg = {
    priority: {
      label: "PRIORITY",
      cls: "bg-[#DDE7F1] text-[#1F3A57] border-[#B4CDEB]",
      icon: "★",
    },
    vip: {
      label: "VIP",
      cls: "bg-gradient-to-r from-[#EEE0F5] to-[#D8B7E8] text-[#4A1976] border-[#B589D6]",
      icon: "♛",
    },
    vvip: {
      label: "VVIP",
      cls: "bg-gradient-to-r from-[#FFF3C4] via-[#FFD86E] to-[#F5B400] text-[#5A4600] border-[#E0AC00] shadow-sm",
      icon: "♛",
    },
  }[t] || null;
  if (!cfg) return null;
  const px = size === "lg" ? "px-2 py-0.5 text-[10px]" : "px-1.5 py-[1px] text-[9px]";
  return (
    <span data-testid={testid || `vip-badge-${t}`}
          title={`Priority tier: ${cfg.label}`}
          className={`inline-flex items-center gap-0.5 rounded-full border uppercase font-bold tracking-[0.12em] ${px} ${cfg.cls}`}>
      <span aria-hidden="true" className="text-[11px] leading-none">{cfg.icon}</span>
      <span>{cfg.label}</span>
    </span>
  );
}

// Iter 157 — Compact hints strip that sits directly under the chat
// header. Gives the agent instant context: last SR date+status, next
// active health request, last invoice / total unpaid dues, LSGD +
// ward. Data comes from /crm/chat/customer-hints/{cid}.
function CustomerHintsStrip({ hints }) {
  const cust = hints?.customer || {};
  const loc = hints?.location || {};
  const sr = hints?.last_service_request;
  const health = hints?.active_health_request;
  const inv = hints?.last_invoice;
  const due = hints?.due || { amount: 0, count: 0 };
  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(undefined, {
        day: "2-digit", month: "short", year: "2-digit",
      });
    } catch { return "—"; }
  };
  const daysAgo = (iso) => {
    if (!iso) return null;
    try {
      const ms = Date.now() - new Date(iso).getTime();
      const d = Math.floor(ms / 86400000);
      if (d <= 0) return "today";
      if (d === 1) return "1d ago";
      return `${d}d ago`;
    } catch { return null; }
  };
  return (
    <div className="px-3 py-2 border-b border-[#E5E2D9] bg-[#F7F6F2]"
         data-testid="chat-customer-hints">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
        <HintCell label="Last SR"
                  testid="hint-last-sr"
                  value={sr ? (sr.no || sr.id?.slice(0, 6)) : "None"}
                  sub={sr ? `${sr.status || "—"} · ${daysAgo(sr.created_at) || fmtDate(sr.created_at)}` : "no service requests yet"}/>
        <HintCell label="Active health"
                  testid="hint-active-health"
                  value={health ? (health.type || "Booking") : "None"}
                  sub={health
                    ? `${health.status || "—"} · ${fmtDate(health.scheduled_at)}${health.partner_name ? " · " + health.partner_name : ""}`
                    : "no active health bookings"}/>
        <HintCell label="Last bill"
                  testid="hint-last-bill"
                  value={inv ? `₹${Number(inv.total || 0).toLocaleString("en-IN")}` : "—"}
                  sub={inv
                    ? `${inv.payment_status || "—"} · ${fmtDate(inv.created_at)}`
                    : "no invoices yet"}
                  tone={inv?.payment_status === "unpaid" ? "warn" : "default"}/>
        <HintCell label={`Dues${due.count ? ` · ${due.count}` : ""}`}
                  testid="hint-dues"
                  value={due.amount > 0 ? `₹${Number(due.amount).toLocaleString("en-IN")}` : "None"}
                  sub={due.amount > 0 ? "unpaid invoices" : "wallet clean"}
                  tone={due.amount > 0 ? "danger" : "success"}/>
      </div>
      <div className="mt-1.5 flex items-center gap-2 text-[10px] text-[#5C5A55] flex-wrap"
           data-testid="hint-location">
        {cust.code && (
          <span className="font-mono bg-white border border-[#E5E2D9] px-1.5 py-0.5 rounded"
                data-testid="hint-code">{cust.code}</span>
        )}
        <VipBadge tier={cust.priority_tier} testid="hint-tier"/>
        {loc.lsgd && (
          <span>LSGD · <strong className="text-[#1A1A1A]">{loc.lsgd}</strong></span>
        )}
        {loc.ward && (
          <span>· {loc.ward}</span>
        )}
      </div>
      {hints?.chat_insights && <ChatInsightsPanel insights={hints.chat_insights}/>}
    </div>
  );
}

// Iter 158 — AI-derived customer insights (populated by the auto-AI
// reply pipeline). Renders one row of chips: sentiment · language ·
// interests · family. Collapsed by default; expand for full summary.
function ChatInsightsPanel({ insights }) {
  const [expanded, setExpanded] = useState(false);
  const sentimentCls = {
    positive: "bg-[#EFF5EC] border-[#B5D5AC] text-[#1A3317]",
    neutral:  "bg-[#DDE7F1] border-[#B4CDEB] text-[#1F3A57]",
    negative: "bg-[#FBEBE5] border-[#EFC7B6] text-[#7A2E18]",
  }[insights.sentiment] || "bg-white border-[#E5E2D9] text-[#5C5A55]";
  const chips = [
    ...(insights.interests || []).slice(0, 3).map(t => ({ label: t, tone: "interest" })),
    ...(insights.family || []).slice(0, 2).map(t => ({ label: t, tone: "family" })),
    ...(insights.topics || []).slice(0, 2).map(t => ({ label: t, tone: "topic" })),
  ];
  const toneCls = {
    interest: "bg-[#FEF7E0] border-[#F0DDB2] text-[#5A4600]",
    family:   "bg-[#F5EAF7] border-[#D8B7E8] text-[#4A1976]",
    topic:    "bg-[#E7F3E4] border-[#B5D5AC] text-[#1A3317]",
  };
  if (chips.length === 0 && !insights.summary) return null;
  return (
    <div className="mt-2 pt-2 border-t border-dashed border-[#E5E2D9]"
         data-testid="hint-chat-insights">
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="text-[9px] uppercase tracking-[0.14em] text-[#8A8780] font-semibold">
          AI · Insights
        </span>
        {insights.sentiment && (
          <span data-testid="hint-sentiment"
                className={`text-[9px] uppercase tracking-[0.14em] font-semibold border px-1.5 py-[1px] rounded-full ${sentimentCls}`}>
            {insights.sentiment}
          </span>
        )}
        {insights.preferred_language && (
          <span data-testid="hint-language"
                className="text-[9px] uppercase tracking-[0.12em] font-semibold border border-[#E5E2D9] bg-white px-1.5 py-[1px] rounded-full text-[#5C5A55]">
            {insights.preferred_language}
          </span>
        )}
        {chips.map((c, i) => (
          <span key={`${c.tone}-${i}`}
                data-testid={`hint-chip-${c.tone}-${i}`}
                title={c.label}
                className={`text-[10px] font-medium border px-1.5 py-[1px] rounded-full max-w-[160px] truncate ${toneCls[c.tone]}`}>
            {c.label}
          </span>
        ))}
        {insights.summary && (
          <button onClick={() => setExpanded(v => !v)}
                  data-testid="hint-insights-toggle"
                  className="ml-auto text-[10px] text-[#2D5A27] underline">
            {expanded ? "Hide" : "Full summary"}
          </button>
        )}
      </div>
      {expanded && insights.summary && (
        <div className="mt-1.5 text-[11px] leading-relaxed text-[#3A3A3A] bg-white border border-[#E5E2D9] rounded p-2"
             data-testid="hint-insights-summary">
          {insights.summary}
        </div>
      )}
    </div>
  );
}

function HintCell({ label, value, sub, testid, tone = "default" }) {
  const toneCls = {
    default: "bg-white border-[#E5E2D9] text-[#1A1A1A]",
    warn:    "bg-[#FDF6E4] border-[#F0DDB2] text-[#5A4600]",
    danger:  "bg-[#FBEBE5] border-[#EFC7B6] text-[#7A2E18]",
    success: "bg-[#EFF5EC] border-[#B5D5AC] text-[#1A3317]",
  }[tone] || "bg-white border-[#E5E2D9] text-[#1A1A1A]";
  return (
    <div data-testid={testid}
         className={`px-2 py-1.5 rounded-md border ${toneCls}`}>
      <div className="text-[9px] uppercase tracking-[0.14em] opacity-70">{label}</div>
      <div className="font-semibold text-sm leading-tight truncate" title={value}>{value}</div>
      {sub && <div className="text-[10px] opacity-70 truncate" title={sub}>{sub}</div>}
    </div>
  );
}

// -------- Right-pane: live chat --------
function ChatPane({ cid, aiGlobalEnabled = true, onClose, onDirty }) {
  const [conv, setConv] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [draft, setDraft] = useState("");
  const [custCtx, setCustCtx] = useState({});
  const [sending, setSending] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [autoAiBusy, setAutoAiBusy] = useState(false);
  const [history, setHistory] = useState(null);   // null | [] | [convs]
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyMsgs, setHistoryMsgs] = useState({}); // conv_id -> messages[]
  const [tasks, setTasks] = useState([]);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  // Iter 157 — customer hints panel (last SR, active health req, dues,
  // location, VIP tier). Fetched only when the active conv has a
  // registered customer_id.
  const [hints, setHints] = useState(null);
  const scrollRef = useRef(null);
  const endRef = useRef(null);
  const lastAtRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  const loadInitial = useCallback(async () => {
    const [cData, m] = await Promise.all([
      api.get("/chat/conversations").then(r => r.data.find(x => x.id === cid)),
      api.get(`/chat/conversations/${cid}/messages`),
    ]);
    setConv(cData || null);
    setMsgs(m.data);
    lastAtRef.current = m.data.length ? m.data[m.data.length - 1].created_at : null;
  }, [cid]);

  const poll = useCallback(async () => {
    if (document.hidden) return;
    try {
      const since = lastAtRef.current;
      const url = since
        ? `/chat/conversations/${cid}/messages?since=${encodeURIComponent(since)}`
        : `/chat/conversations/${cid}/messages`;
      const r = await api.get(url);
      if (r.data.length) {
        setMsgs(prev => {
          const seen = new Set(prev.map(m => m.id));
          return [...prev, ...r.data.filter(m => !seen.has(m.id))];
        });
        lastAtRef.current = r.data[r.data.length - 1].created_at;
      }
    } catch (_) { /* ignore */ }
  }, [cid]);

  useEffect(() => { loadInitial(); }, [loadInitial]);
  useEffect(() => {
    const t = setInterval(poll, POLL_MS);
    return () => clearInterval(t);
  }, [poll]);

  // Iter 157 — load customer hints when the conversation resolves to a
  // registered customer. Non-customers (visitors / leads) skip the
  // hints panel — nothing meaningful to show yet.
  useEffect(() => {
    setHints(null);
    if (!conv?.customer_id) return;
    let alive = true;
    api.get(`/crm/chat/customer-hints/${conv.customer_id}`)
      .then(r => { if (alive) setHints(r.data); })
      .catch(() => { if (alive) setHints(null); });
    return () => { alive = false; };
  }, [conv?.customer_id]);

  // Iter 138 — Typing indicator. Poll customer typing state, and POST
  // our own typing state (throttled to 3s) so the customer sees us
  // composing.
  const [custTyping, setCustTyping] = useState({ active: false, name: "" });
  const typingLastPostRef = useRef(0);
  useEffect(() => {
    if (!cid) return;
    let alive = true;
    const pollT = async () => {
      if (document.hidden) return;
      try {
        const r = await api.get(`/chat/conversations/${cid}/typing`);
        if (alive) setCustTyping({
          active: !!r.data?.customer_typing,
          name: r.data?.customer_name || "",
        });
      } catch { /* ignore */ }
    };
    pollT();
    const iv = setInterval(pollT, POLL_MS);
    return () => { alive = false; clearInterval(iv); };
  }, [cid]);
  const notifyTyping = () => {
    const now = Date.now();
    if (now - typingLastPostRef.current < 3000) return;
    typingLastPostRef.current = now;
    api.post(`/chat/conversations/${cid}/typing`).catch(() => {});
  };
  useEffect(() => {
    // Iter 112 — robust auto-scroll to bottom using a sentinel div, with
    // double-RAF to also catch late-arriving fonts/images that resize
    // bubbles after paint.
    const jump = () => {
      if (endRef.current) {
        endRef.current.scrollIntoView({ block: "end", behavior: "smooth" });
      } else if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    };
    requestAnimationFrame(jump);
    requestAnimationFrame(() => requestAnimationFrame(jump));
  }, [msgs]);

  useEffect(() => {
    if (!conv?.customer_id) { setCustCtx({}); return; }
    api.get(`/chat/assist/customer-context/${conv.customer_id}`).then(r => setCustCtx(r.data)).catch(() => setCustCtx({}));
  }, [conv?.customer_id]);

  const send = async () => {
    const t = draft.trim();
    if (!t || sending) return;
    setSending(true);
    try {
      await api.post(`/chat/conversations/${cid}/messages`, { text: t });
      setDraft("");
      await poll();
      onDirty?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally {
      setSending(false);
      // Iter 95 — keep composer visible + refocus so staff can keep typing
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  };

  // Iter 139 — Ask AI to draft an English reply based on the recent chat
  const aiSuggest = async () => {
    if (aiBusy) return;
    setAiBusy(true);
    try {
      const hint = draft.trim() || null;
      const r = await api.post(`/chat/conversations/${cid}/ai-suggest`,
                                 hint ? { hint } : {});
      if (r.data?.reply) {
        setDraft(r.data.reply);
        requestAnimationFrame(() => inputRef.current?.focus());
        toast.success("AI reply drafted — edit and send");
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAiBusy(false); }
  };

  const toggleAutoAi = async () => {
    if (autoAiBusy) return;
    setAutoAiBusy(true);
    try {
      const next = !conv?.auto_ai_enabled;
      await api.put(`/chat/conversations/${cid}/auto-ai`, { enabled: next });
      setConv((c) => c ? { ...c, auto_ai_enabled: next } : c);
      toast.success(next ? "Auto-AI reply is ON" : "Auto-AI reply is OFF");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAutoAiBusy(false); }
  };

  const loadTasks = useCallback(async () => {
    try {
      // Iter 96 — display CRM Tickets linked to this conversation
      // (chat_conversation_id === cid) instead of follow-up tasks.
      const r = await api.get(`/tickets?chat_conversation_id=${cid}`);
      setTasks(r.data);
    } catch { /* silent — tickets list is optional */ }
  }, [cid]);
  useEffect(() => { if (cid) loadTasks(); }, [cid, loadTasks]);

  const closeConv = async () => {
    if (!window.confirm("Close this conversation?")) return;
    try {
      await api.post(`/chat/conversations/${cid}/close`);
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Iter 215 — mark this conversation unread again so it re-appears in
  // the Unread filter + re-arms the bell. Handy when the operator
  // clicked into a thread by mistake, or wants to come back later.
  const markUnread = async () => {
    try {
      await api.post(`/chat/conversations/${cid}/mark-unread`);
      toast.success("Marked as unread");
      onClose();       // pop back to the conversation list
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!conv) return <div className="flex-1 p-6 text-sm text-[#8A8780]">Loading…</div>;

  return (
    <>
      {/* Header */}
      <div className="px-4 py-3 border-b border-[#E5E2D9] flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-[#DDE7F1] text-[#1F3A57] flex items-center justify-center">
          <User size={16} weight="fill"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading text-base leading-tight flex items-center gap-1.5 flex-wrap">
            <span className="truncate">{conv.customer_name}</span>
            <VipBadge tier={conv.customer_priority_tier} size="lg"
                      testid="chat-header-vip"/>
          </div>
          <div className="text-[11px] text-[#8A8780]">
            {conv.customer_phone} · Status: <strong>{conv.status}</strong>
            {conv.assigned_staff_name && <> · Assigned: {conv.assigned_staff_name}</>}
          </div>
        </div>
        <a href={`${API_BASE}/chat/conversations/${cid}/export?format=csv`}
            target="_blank" rel="noreferrer"
            data-testid="chat-export-csv"
            title="Download conversation as CSV"
            className="p-2 rounded hover:bg-[#F7F6F2] text-[#5C5A55]">
          <DownloadSimple size={16} weight="fill"/>
        </a>
        {conv.customer_phone && (
          <button onClick={async () => {
                    if (history === null) {
                      try {
                        const r = await api.get(
                          `/chat/conversations/by-phone?phone=${encodeURIComponent(conv.customer_phone)}`
                        );
                        setHistory(r.data.filter(x => x.id !== cid));
                      } catch (e) { toast.error(formatApiError(e)); return; }
                    }
                    setHistoryOpen(v => !v);
                  }}
                  data-testid="chat-view-history-btn"
                  title="View previous conversations from this phone"
                  className="p-2 rounded hover:bg-[#F7F6F2] text-[#1F3A57] flex items-center gap-1 text-[11px]">
            <ChatCircle size={16} weight="fill"/>
            <span className="hidden md:inline">History</span>
            {history?.length > 0 && (
              <span className="text-[9px] font-bold bg-[#DDE7F1] text-[#1F3A57] px-1 rounded">
                {history.length}
              </span>
            )}
          </button>
        )}
        <button onClick={() => setShowTicketModal(true)}
                data-testid="chat-create-ticket-btn"
                title="Create a new complaint ticket from this chat"
                className="p-2 rounded hover:bg-[#F7F6F2] text-[#7A2E18] flex items-center gap-1 text-[11px]">
            <Ticket size={16} weight="fill"/>
            <span className="hidden md:inline">Create Ticket</span>
        </button>
        <button onClick={toggleAutoAi} disabled={autoAiBusy}
                data-testid="chat-auto-ai-toggle"
                style={{ display: aiGlobalEnabled ? undefined : "none" }}
                title={conv.auto_ai_enabled
                  ? "Auto-AI is ON — customer messages get an AI reply automatically. Click to turn off."
                  : "Auto-AI is OFF — enable to let AI reply to this customer on your behalf."}
                className={`p-2 rounded flex items-center gap-1 text-[11px] disabled:opacity-60 ${
                  conv.auto_ai_enabled
                    ? "bg-[#EFF5EC] text-[#1A3317] hover:bg-[#DDEBD7]"
                    : "text-[#5C5A55] hover:bg-[#F7F6F2]"
                }`}>
          <Robot size={16} weight={conv.auto_ai_enabled ? "fill" : "regular"}/>
          <span className="hidden md:inline">
            Auto AI · {conv.auto_ai_enabled ? "ON" : "OFF"}
          </span>
        </button>
        {conv.customer_id && (
          <button onClick={() => {
                    const params = new URLSearchParams({
                      customer_id: conv.customer_id,
                      description: conv.last_message_preview || "Follow-up from chat",
                      source_chat_id: cid,
                    });
                    navigate(`/operations/service-requests/new?${params}`);
                  }}
                  data-testid="chat-create-sr-btn"
                  title="Create a Service Request for this customer"
                  className="p-2 rounded hover:bg-[#F7F6F2] text-[#2D5A27] flex items-center gap-1 text-[11px]">
            <Truck size={16} weight="fill"/>
            <span className="hidden md:inline">Create SR</span>
          </button>
        )}
        {conv.is_public_visitor && !conv.lead_id && (
          <button onClick={() => setShowLeadModal(true)}
                  data-testid="chat-create-lead-btn"
                  title="Create a CRM lead from this new-customer chat"
                  className="p-2 rounded hover:bg-[#F7F6F2] text-[#1A4E12] flex items-center gap-1 text-[11px]">
            <UserCirclePlus size={16} weight="fill"/>
            <span className="hidden md:inline">Create Lead</span>
          </button>
        )}
        {conv.lead_id && !conv.customer_id && (
          <button onClick={() => setShowConvertModal(true)}
                  data-testid="chat-convert-lead-btn"
                  title="Convert this lead to a customer (chat history preserved)"
                  className="p-2 rounded hover:bg-[#EDF7EA] text-[#1A4E12] flex items-center gap-1 text-[11px] bg-[#F0F7EE]">
            <ArrowsClockwise size={16} weight="fill"/>
            <span className="hidden md:inline">Convert</span>
          </button>
        )}
        {conv.lead_id && conv.customer_id && (
          <span className="p-2 rounded bg-[#EDF7EA] text-[#1A4E12] text-[10px] font-semibold flex items-center gap-1"
                data-testid="chat-converted-badge"
                title="This lead has been converted to a customer">
            ✓ Converted
          </span>
        )}
        <button onClick={markUnread}
                data-testid="chat-mark-unread-btn"
                title="Mark this conversation as unread"
                className="p-2 rounded hover:bg-[#EDF7EA] text-[#2D5A27]">
          <EnvelopeSimple size={16}/>
        </button>
        <button onClick={closeConv}
                data-testid="chat-close-btn"
                title="Close conversation"
                className="p-2 rounded hover:bg-[#F9EDE7] text-[#7A2E18]">
          <XCircle size={16} weight="fill"/>
        </button>
      </div>

      {/* Iter 157 — Customer hints strip (last SR / active health / dues / location).
          Renders only when the conversation has a registered customer. */}
      {hints && <CustomerHintsStrip hints={hints}/>}

      {/* History drawer — past conversations from same phone */}
      {historyOpen && history && (
        <div className="border-b border-[#E5E2D9] bg-[#FAFAF7]"
             data-testid="chat-history-drawer">
          <div className="px-4 py-2 flex items-center justify-between text-xs text-[#5C5A55] bg-[#F0EFEA]">
            <span>Past conversations from {conv.customer_phone} ({history.length})</span>
            <button onClick={() => setHistoryOpen(false)}
                    data-testid="chat-history-close"
                    className="text-[#7A2E18] hover:underline">Hide</button>
          </div>
          {history.length === 0 && (
            <div className="px-4 py-3 text-xs text-[#8A8780] italic">
              No previous conversations from this phone number.
            </div>
          )}
          <div className="max-h-56 overflow-y-auto divide-y divide-[#F0EEE6]">
            {history.map(h => (
              <div key={h.id} className="px-4 py-2">
                <button onClick={async () => {
                          if (!historyMsgs[h.id]) {
                            try {
                              const r = await api.get(`/chat/conversations/${h.id}/messages`);
                              setHistoryMsgs(prev => ({ ...prev, [h.id]: r.data }));
                            } catch (e) { toast.error(formatApiError(e)); }
                          } else {
                            setHistoryMsgs(prev => {
                              const c = { ...prev }; delete c[h.id]; return c;
                            });
                          }
                        }}
                        data-testid={`chat-history-item-${h.id}`}
                        className="w-full flex items-center gap-2 text-left">
                  <StatusDot status={h.status}/>
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] text-[#5C5A55] truncate">
                      {h.last_message_preview || "…"}
                    </div>
                    <div className="text-[10px] text-[#8A8780]">
                      {h.status} · {new Date(h.last_message_at).toLocaleString()}
                      {h.assigned_staff_name && <> · {h.assigned_staff_name}</>}
                      {h.visitor_rating != null && <> · Rated {h.visitor_rating}★</>}
                    </div>
                  </div>
                </button>
                {historyMsgs[h.id] && (
                  <div className="mt-2 space-y-1 max-h-40 overflow-y-auto bg-white border border-[#E5E2D9] rounded p-2">
                    {historyMsgs[h.id].map(m => (
                      <div key={m.id} className="text-[10px]">
                        <span className="font-semibold text-[#2D5A27]">{m.sender_name}:</span>
                        <span className="ml-1 text-[#1A1A1A]">{linkify(m.text)}</span>
                        <span className="ml-1 text-[#8A8780]">({new Date(m.created_at).toLocaleString()})</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Message list */}
      <div ref={scrollRef}
           className="flex-1 min-h-0 overflow-y-auto p-4 space-y-2 bg-[#FAFAF7] chat-scroll"
           data-testid="chat-messages">
        {msgs.map(m => <StaffBubble key={m.id} m={m}/>)}
        {/* Bottom sentinel for auto-scroll (Iter 112) */}
        <div ref={endRef} data-testid="chat-messages-end"/>
      </div>

      {/* Composer — ALWAYS visible, never hidden after send */}
      <div className="border-t border-[#E5E2D9] p-3 bg-white shrink-0"
           data-testid="chat-composer">
        {tasks.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-1.5">
            {tasks.slice(0, 3).map(t => (
              <a key={t.id} href={`/crm/tickets?ticket=${t.ticket_no || t.id}`}
                 target="_blank" rel="noreferrer"
                 data-testid={`chat-linked-ticket-${t.id}`}
                 title={t.issue || t.ticket_no}
                 className={`text-[10px] px-2 py-0.5 rounded-full border ${
                   t.status === "resolved" || t.status === "closed"
                     ? "bg-[#EDF7EA] border-[#B5D5AC] text-[#1A4E12]"
                     : t.priority === "high"
                       ? "bg-[#FBEBE5] border-[#EFC7B6] text-[#7A2E18]"
                       : "bg-[#FDF6E4] border-[#F0DDB2] text-[#5A4600]"
                 }`}>
                🎫 {t.ticket_no || "TKT"} · {t.status}
              </a>
            ))}
            {tasks.length > 3 && (
              <span className="text-[10px] text-[#8A8780]">+{tasks.length - 3} more</span>
            )}
          </div>
        )}
        {/* Iter 138 — Customer typing indicator */}
        {custTyping.active && (
          <div className="flex items-center gap-2 text-[11px] text-[#5C5A55] italic mb-1"
               data-testid="crm-cust-typing">
            <span className="typing-dot-pulse inline-flex gap-0.5">
              <span/><span/><span/>
            </span>
            {custTyping.name || "Customer"} is typing…
          </div>
        )}
        {/* Iter 138 — Quick templates chip rail. Shows the top 5 most-
            used templates so agents can drop a canned reply in one click
            without opening the full picker. */}
        <QuickTemplatesRail
          onPick={(t) => {
            const ctx = {
              ...custCtx,
              customer_name:  custCtx.customer_name || conv.customer_name,
              customer_phone: conv.customer_phone,
              customer_email: conv.customer_email,
              ticket_no:      tasks[0]?.ticket_no,
              agent_name:     user?.name || user?.email,
            };
            const filled = substituteTokens(t.body, ctx);
            setDraft(prev => prev && prev.trim() ? `${prev.trim()}\n\n${filled}` : filled);
            requestAnimationFrame(() => inputRef.current?.focus());
            api.post(`/reply-templates/${t.id}/use`).catch(() => {});
          }}/>
        <LiveSuggestion draft={draft} conversationId={cid}
                        onUse={(t) => { setDraft(t); requestAnimationFrame(() => inputRef.current?.focus()); }} />
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            value={draft} onChange={e => { setDraft(e.target.value); if (e.target.value) notifyTyping(); }}
            onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Reply to customer… (Enter to send · Shift+Enter for newline)"
            rows={5}
            autoFocus
            data-testid="chat-input"
            className="flex-1 min-h-[140px] max-h-[280px] px-3 py-2.5 border border-[#E5E2D9] rounded-lg text-sm resize-y focus:border-[#2D5A27] outline-none"
          />
          <TemplatePicker onPick={(t) => {
            // Iter 114 — substitute personalisation tokens using the
            // current chat/ticket context, then record usage on the
            // template so admins see which canned replies are earning
            // their keep.
            const ctx = {
              ...custCtx,
              customer_name:  custCtx.customer_name || conv.customer_name,
              customer_phone: conv.customer_phone,
              customer_email: conv.customer_email,
              ticket_no:      tasks[0]?.ticket_no,
              agent_name:     user?.name || user?.email,
            };
            const filled = substituteTokens(t.body, ctx);
            setDraft(prev => prev && prev.trim() ? `${prev.trim()}\n\n${filled}` : filled);
            requestAnimationFrame(() => inputRef.current?.focus());
            // fire-and-forget usage bump — never block the composer on it
            api.post(`/reply-templates/${t.id}/use`).catch(() => {});
          }}/>
          {aiGlobalEnabled && (
            <button onClick={aiSuggest} disabled={aiBusy}
                    data-testid="chat-ai-suggest"
                    title="Ask AI to draft a reply based on the chat so far"
                    className="h-10 w-10 rounded-full border border-[#0B2B66] text-[#0B2B66] hover:bg-[#E4EAF5] disabled:opacity-60 flex items-center justify-center shrink-0">
              <Sparkle size={16} weight={aiBusy ? "regular" : "fill"}
                       className={aiBusy ? "animate-pulse" : ""}/>
            </button>
          )}
          <TranslateDraftButton draft={draft} setDraft={setDraft} />
          <MagicRewrite draft={draft}
                        onAccept={(t) => setDraft(t)}
                        testidPrefix="chat"
                        size="md"/>
          <button onClick={send} disabled={sending || !draft.trim()}
                  data-testid="chat-send"
                  className="h-10 w-10 rounded-full bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-60 text-white flex items-center justify-center shrink-0">
            <PaperPlaneRight size={16} weight="fill"/>
          </button>
        </div>
      </div>

      {/* Modals — Create Ticket / Create Lead / Convert to Customer */}
      {showTicketModal && (
        <ChatTicketModal cid={cid} conv={conv}
          onClose={() => setShowTicketModal(false)}
          onCreated={() => { setShowTicketModal(false); loadTasks(); poll(); }}/>
      )}
      {showLeadModal && (
        <CreateLeadModal cid={cid} conv={conv}
          onClose={() => setShowLeadModal(false)}
          onCreated={(lead) => {
            setShowLeadModal(false);
            setConv(c => c ? { ...c, lead_id: lead.id } : c);
            poll();
            toast.success(`Lead created · ${lead.name}`);
          }}/>
      )}
      {showConvertModal && (
        <ConvertLeadModal lead_id={conv?.lead_id}
          onClose={() => setShowConvertModal(false)}
          onDone={(customer) => {
            setShowConvertModal(false);
            toast.success(`Customer created · ${customer.name}`);
            navigate(`/customers/${customer.id}`);
          }}/>
      )}
    </>
  );
}


// -------- Iter 138: Quick templates chip rail ---------------------
// Renders the 5 most-used reply templates as one-click chips right
// above the composer. Full picker still available for the long tail.
function QuickTemplatesRail({ onPick }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get("/reply-templates").then(r => {
      if (!alive) return;
      const list = (r.data || [])
        .filter(t => t.is_active !== false && !t.is_auto_ack)
        .sort((a, b) => (b.use_count || 0) - (a.use_count || 0))
        .slice(0, 5);
      setItems(list);
    }).catch(() => setItems([]));
    return () => { alive = false; };
  }, []);
  if (items === null) return null;
  if (items.length === 0) {
    return (
      <div className="mb-2 text-[10px] text-[#8A8780] italic" data-testid="chat-quick-templates-empty">
        No reply templates saved yet.{" "}
        <a href="/settings/reply-templates" className="underline text-[#2D5A27]">Create your first →</a>
      </div>
    );
  }
  return (
    <div className="mb-2 flex items-center gap-1.5 overflow-x-auto pb-1"
         data-testid="chat-quick-templates">
      <span className="text-[9px] uppercase tracking-[0.16em] text-[#8A8780] shrink-0 pr-1">
        Templates
      </span>
      {items.map(t => (
        <button key={t.id} type="button"
                onClick={() => onPick(t)}
                data-testid={`chat-quick-template-${t.id}`}
                title={t.body}
                className="shrink-0 inline-flex items-center gap-1 h-7 px-2.5 rounded-full border border-[#B5D5AC] bg-[#F0F7EE] hover:bg-[#DBEBD3] text-[#1A3317] text-[11px] font-medium max-w-[240px]">
          <ChatText size={11} weight="bold" className="shrink-0"/>
          <span className="truncate">{t.title}</span>
        </button>
      ))}
    </div>
  );
}


// -------- Template Picker moved to /components/TemplatePicker (Iter 116) --------

function StaffBubble({ m }) {
  if (m.sender === "system") {
    return (
      <div className="flex justify-center">
        <div className="text-[11px] italic text-[#8A8780] text-center max-w-[80%]">{linkify(m.text)}</div>
      </div>
    );
  }
  const mineLike = m.sender === "staff" || m.sender === "bot";
  const bot = m.sender === "bot";
  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(m.text || "");
      toast.success("Message copied");
    } catch { toast.error("Copy blocked by browser"); }
  };
  return (
    <div className={`flex group ${mineLike ? "justify-end" : "justify-start"}`}>
      <div className={`relative max-w-[75%] rounded-2xl px-3 py-2 text-sm ${
        bot
          ? "bg-[#F0F7EE] text-[#1A3317] border border-[#D4E0D1]"
          : mineLike
            ? "bg-[#2D5A27] text-white rounded-br-none"
            : "bg-white text-[#1A1A1A] border border-[#E5E2D9] rounded-bl-none"
      }`}>
        <button type="button"
                onClick={copyText}
                data-testid={`chat-copy-${m.id}`}
                title="Copy message"
                className={`absolute -top-2 ${mineLike ? "-left-2" : "-right-2"} opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6 rounded-full border shadow-sm inline-flex items-center justify-center ${
                  mineLike
                    ? "bg-white text-[#2D5A27] border-[#D4E0D1] hover:bg-[#F0F7EE]"
                    : "bg-white text-[#5C5A55] border-[#E5E2D9] hover:bg-[#F7F6F2]"
                }`}>
          <Copy size={12} weight="bold"/>
        </button>
        {m.text && !String(m.id).startsWith("opt") && (
          <Link to={`/crm/tasks?message=${m.id}`} data-testid={`chat-make-task-${m.id}`} title="Make a task from this message"
                className={`absolute -bottom-2 ${mineLike ? "-left-2" : "-right-2"} opacity-0 group-hover:opacity-100 transition-opacity h-6 px-1.5 rounded-full border shadow-sm inline-flex items-center text-[10px] font-bold bg-white text-[#B91C1C] border-[#FEE2E2] hover:bg-[#FEF2F2]`}>
            + Task
          </Link>
        )}
        <div className={`text-[10px] uppercase tracking-[0.12em] mb-0.5 ${
          bot ? "text-[#2D5A27]" : mineLike ? "text-white/80" : "text-[#5C5A55]"
        }`}>
          {bot && <RigoAvatar size={12} ring={false} className="inline-block align-middle mr-0.5"/>}
          {m.sender_name} · {m.sender}
        </div>
        <div className="whitespace-pre-wrap leading-snug">{linkify(m.text)}</div>
        {m.text && m.sender !== "system" && <TranslateMessage m={m} tone={m.sender === "staff" ? "text-white" : "text-[#0B2B66]"} />}
        <div className={`text-[9px] mt-0.5 opacity-70 ${m.sender === "staff" ? "text-white" : "text-[#5C5A55]"}`}>
          {new Date(m.created_at).toLocaleString()}
        </div>
      </div>
    </div>
  );
}


// ============================================================
// ==============  MODALS (Iter 95)  ==========================
// ============================================================

function ModalShell({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
         onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl"
           onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-[#E5E2D9] flex items-center justify-between">
          <h3 className="font-heading text-lg">{title}</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-[#F7F6F2]"
                  data-testid="modal-close-btn">
            <XCircle size={16} className="mx-auto"/>
          </button>
        </div>
        <div className="p-4 space-y-3">{children}</div>
      </div>
    </div>
  );
}

/**
 * Iter 101 — Chat's "Create Ticket" now uses the FULL Tickets.jsx
 * TicketFormModal (phone autocomplete, quick notes, customer fact
 * chips, multi-assignee list) instead of its old stripped-down modal.
 * We simply prefill from the chat conversation and inject
 * chat_conversation_id when submitting.
 */
function ChatTicketModal({ cid, conv, onClose, onCreated }) {
  const [form, setForm] = useState({
    ...emptyForm,
    customer_id: conv?.customer_id || "",
    customer_name: conv?.customer_name || "",
    phone: conv?.customer_phone || "",
    email: conv?.customer_email || "",
    issue: conv?.last_message_preview || "",
    source: "contact_form",   // "Chat" preselected
  });
  const [lookup, setLookup] = useState(
    conv?.customer_id
      ? { state: "found",
          customer: { id: conv.customer_id, name: conv.customer_name,
                       phone: conv.customer_phone, email: conv.customer_email } }
      : { state: "idle", customer: null }
  );
  const [users, setUsers] = useState([]);

  useEffect(() => {
    api.get("/auth/users").then(r => {
      const uu = (r.data || []).filter(u =>
        u.active !== false && u.role !== "superadmin" &&
        ["office_staff", "office", "field_staff", "service_coordinator",
         "manager", "admin"].includes(u.role)
      );
      setUsers(uu);
    }).catch(() => setUsers([]));
  }, []);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.issue.trim()) { toast.error("Describe the issue"); return; }
    if (!form.customer_id && !form.customer_name.trim()) {
      toast.error("Pick a customer via phone lookup or type a walk-in name");
      return;
    }
    try {
      await api.post("/tickets", {
        ...form,
        customer_id: form.customer_id || null,
        chat_conversation_id: cid,
      });
      toast.success("Ticket created");
      onCreated?.();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <TicketFormModal
      form={form} setForm={setForm}
      lookup={lookup} setLookup={setLookup}
      users={users}
      onClose={onClose}
      onSubmit={submit}
      title={`New Ticket · ${conv?.customer_name || "Chat"}`}
    />
  );
}


function CreateLeadModal({ cid, conv, onClose, onCreated }) {
  const [name, setName] = useState(conv?.customer_name || "");
  const [phone, setPhone] = useState(conv?.customer_phone || "");
  const [email, setEmail] = useState(conv?.customer_email || "");
  const [location, setLocation] = useState(conv?.lsgd_hint || "");
  const [topic, setTopic] = useState(conv?.topic || "new_service");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!phone.trim()) { toast.error("Phone is required"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/chat/conversations/${cid}/lead`, {
        name: name.trim() || null, phone: phone.trim(),
        email: email.trim() || null, location: location.trim() || null,
        topic, notes: notes.trim() || null,
      });
      onCreated?.(r.data.lead);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <ModalShell title="Save as CRM Lead" onClose={onClose}>
      <div className="text-xs text-[#5C5A55] bg-[#FDF6E4] border border-[#F0DDB2] rounded p-2">
        This will save the enquiry under <b>CRM & Leads → Contact Submissions</b> and keep the chat history linked. Idempotent — safe to click multiple times.
      </div>
      <input placeholder="Name" value={name} onChange={e => setName(e.target.value)}
              data-testid="lead-modal-name"
              className="w-full h-11 px-3 border border-[#E5E2D9] rounded-lg text-sm outline-none focus:border-[#2D5A27]"/>
      <input placeholder="Phone" value={phone} onChange={e => setPhone(e.target.value)}
              data-testid="lead-modal-phone"
              className="w-full h-11 px-3 border border-[#E5E2D9] rounded-lg text-sm outline-none focus:border-[#2D5A27]"/>
      <input placeholder="Email (optional)" value={email} onChange={e => setEmail(e.target.value)}
              data-testid="lead-modal-email"
              className="w-full h-11 px-3 border border-[#E5E2D9] rounded-lg text-sm outline-none focus:border-[#2D5A27]"/>
      <input placeholder="Location / area / pincode" value={location} onChange={e => setLocation(e.target.value)}
              data-testid="lead-modal-location"
              className="w-full h-11 px-3 border border-[#E5E2D9] rounded-lg text-sm outline-none focus:border-[#2D5A27]"/>
      <select value={topic} onChange={e => setTopic(e.target.value)}
              data-testid="lead-modal-topic"
              className="w-full h-10 px-2 border border-[#E5E2D9] rounded-lg text-sm">
        <option value="new_service">Book a new pickup</option>
        <option value="billing">Billing / invoices</option>
        <option value="bag_delivery">Bag delivery / PIN</option>
        <option value="support">Report an issue</option>
        <option value="other">Something else</option>
      </select>
      <textarea placeholder="Notes (optional)" rows={2} value={notes} onChange={e => setNotes(e.target.value)}
                data-testid="lead-modal-notes"
                className="w-full px-3 py-2 border border-[#E5E2D9] rounded-lg text-sm outline-none focus:border-[#2D5A27] resize-none"/>
      <button onClick={submit} disabled={busy}
              data-testid="lead-modal-submit"
              className="w-full h-11 rounded-lg bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-60 text-white text-sm font-medium">
        {busy ? "Saving…" : "Save lead"}
      </button>
    </ModalShell>
  );
}

function ConvertLeadModal({ lead_id, onClose, onDone }) {
  const [lsgdName, setLsgdName] = useState("");
  const [ward, setWard] = useState("");
  const [ctype, setCtype] = useState("residential");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/leads/${lead_id}/convert-to-customer`, {
        lsgd_name: lsgdName.trim() || null,
        ward_no: ward.trim() || null,
        customer_type: ctype,
      });
      onDone?.(r.data.customer);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <ModalShell title="Convert Lead → Customer" onClose={onClose}>
      <div className="text-xs text-[#5C5A55] bg-[#EDF7EA] border border-[#B5D5AC] rounded p-2">
        ✓ Chat history, follow-up tasks and lead notes will remain linked to the new customer.
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-[0.15em] text-[#8A8780]">Customer type</label>
        <select value={ctype} onChange={e => setCtype(e.target.value)}
                data-testid="convert-modal-type"
                className="w-full h-10 px-2 border border-[#E5E2D9] rounded-lg text-sm">
          <option value="residential">Residential</option>
          <option value="commercial">Commercial</option>
          <option value="institution">Institution</option>
          <option value="hospital">Hospital</option>
        </select>
      </div>
      <input placeholder="LSGD / Panchayat / Muncipality" value={lsgdName} onChange={e => setLsgdName(e.target.value)}
              data-testid="convert-modal-lsgd"
              className="w-full h-11 px-3 border border-[#E5E2D9] rounded-lg text-sm outline-none focus:border-[#2D5A27]"/>
      <input placeholder="Ward no. (optional)" value={ward} onChange={e => setWard(e.target.value)}
              data-testid="convert-modal-ward"
              className="w-full h-11 px-3 border border-[#E5E2D9] rounded-lg text-sm outline-none focus:border-[#2D5A27]"/>
      <button onClick={submit} disabled={busy}
              data-testid="convert-modal-submit"
              className="w-full h-11 rounded-lg bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-60 text-white text-sm font-medium">
        {busy ? "Converting…" : "Convert to customer"}
      </button>
    </ModalShell>
  );
}

// ============================================================
// ============  BULK EXPORT (Iter 97)  =======================
// ============================================================

const RANGE_OPTIONS = [
  { key: "today", label: "Today" },
  { key: "week",  label: "This week" },
  { key: "30d",   label: "Last 30 days" },
  { key: "all",   label: "All time" },
  { key: "custom",label: "Custom range" },
];

function BulkExportModal({ conversations, onClose }) {
  const [range, setRange] = useState("30d");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo]     = useState("");
  const [format, setFormat]     = useState("zip");
  const [selectAll, setSelectAll] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);

  const filtered = conversations.filter(c => {
    if (!search.trim()) return true;
    const k = search.trim().toLowerCase();
    return `${c.customer_name} ${c.customer_phone}`.toLowerCase().includes(k);
  });

  const toggleOne = (id) => {
    setSelected(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
    setSelectAll(false);
  };

  const download = async () => {
    if (range === "custom" && !dateFrom && !dateTo) {
      toast.error("Pick at least one date for custom range"); return;
    }
    setBusy(true);
    try {
      const params = new URLSearchParams();
      params.set("range", range);
      params.set("format", format);
      if (range === "custom") {
        if (dateFrom) params.set("date_from", dateFrom);
        if (dateTo)   params.set("date_to", dateTo);
      }
      if (!selectAll && selected.size > 0) {
        params.set("conversation_ids", [...selected].join(","));
      }
      const token = localStorage.getItem("rescigo_token") || "";
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/chat/export-bulk?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!r.ok) throw new Error(await r.text());
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const cd = r.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      a.download = m ? m[1] : `rescigo_chats.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Chats downloaded");
      onClose?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
         onClick={onClose} data-testid="chat-bulk-export-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] flex flex-col"
           onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-[#E5E2D9] flex items-center justify-between">
          <h3 className="font-heading text-lg">Download chats</h3>
          <button onClick={onClose}
                  data-testid="bulk-export-close"
                  className="w-8 h-8 rounded-full hover:bg-[#F7F6F2]">
            <XCircle size={16} className="mx-auto"/>
          </button>
        </div>
        <div className="p-4 space-y-4 overflow-y-auto flex-1">
          {/* Range presets */}
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-[#8A8780]">Time range</label>
            <div className="grid grid-cols-5 gap-1.5 mt-1">
              {RANGE_OPTIONS.map(r => (
                <button key={r.key} type="button"
                        data-testid={`bulk-range-${r.key}`}
                        onClick={() => setRange(r.key)}
                        className={`h-9 rounded-md text-[11px] border transition-colors ${
                          range === r.key
                            ? "bg-[#2D5A27] text-white border-[#2D5A27]"
                            : "bg-white text-[#1A1A1A] border-[#E5E2D9] hover:border-[#2D5A27]"
                        }`}>{r.label}</button>
              ))}
            </div>
            {range === "custom" && (
              <div className="grid grid-cols-2 gap-2 mt-2">
                <div>
                  <label className="text-[9px] text-[#8A8780]">From</label>
                  <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                          data-testid="bulk-date-from"
                          className="w-full h-9 px-2 border border-[#E5E2D9] rounded-md text-sm"/>
                </div>
                <div>
                  <label className="text-[9px] text-[#8A8780]">To</label>
                  <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                          data-testid="bulk-date-to"
                          className="w-full h-9 px-2 border border-[#E5E2D9] rounded-md text-sm"/>
                </div>
              </div>
            )}
          </div>

          {/* Format */}
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-[#8A8780]">Format</label>
            <div className="grid grid-cols-3 gap-1.5 mt-1">
              {[
                { key: "csv", label: "CSV (flat)" },
                { key: "txt", label: "TXT (readable)" },
                { key: "zip", label: "ZIP (per-chat + report)" },
              ].map(f => (
                <button key={f.key} type="button"
                        data-testid={`bulk-format-${f.key}`}
                        onClick={() => setFormat(f.key)}
                        className={`h-9 rounded-md text-[11px] border transition-colors ${
                          format === f.key
                            ? "bg-[#2D5A27] text-white border-[#2D5A27]"
                            : "bg-white text-[#1A1A1A] border-[#E5E2D9] hover:border-[#2D5A27]"
                        }`}>{f.label}</button>
              ))}
            </div>
          </div>

          {/* Customer picker */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[10px] uppercase tracking-[0.15em] text-[#8A8780]">
                Customers ({selectAll ? "all" : selected.size} selected)
              </label>
              <label className="flex items-center gap-1.5 text-xs text-[#2D5A27] cursor-pointer">
                <input type="checkbox" checked={selectAll}
                        onChange={e => { setSelectAll(e.target.checked);
                                          if (e.target.checked) setSelected(new Set()); }}
                        data-testid="bulk-select-all"/>
                Include all customers
              </label>
            </div>
            {!selectAll && (
              <>
                <input value={search} onChange={e => setSearch(e.target.value)}
                        placeholder="Search customer / phone"
                        data-testid="bulk-customer-search"
                        className="w-full h-9 px-2 border border-[#E5E2D9] rounded-md text-sm mb-2"/>
                <div className="max-h-56 overflow-y-auto border border-[#E5E2D9] rounded-md divide-y divide-[#F0EEE6]">
                  {filtered.length === 0 && (
                    <div className="text-center text-[11px] text-[#8A8780] py-4">
                      No matching customers.
                    </div>
                  )}
                  {filtered.map(c => (
                    <label key={c.id}
                           data-testid={`bulk-conv-row-${c.id}`}
                           className="flex items-center gap-2 px-3 py-2 hover:bg-[#F7F6F2] cursor-pointer">
                      <input type="checkbox" checked={selected.has(c.id)}
                              onChange={() => toggleOne(c.id)}
                              data-testid={`bulk-conv-check-${c.id}`}/>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm truncate">{c.customer_name || "—"}</div>
                        <div className="text-[10px] text-[#8A8780]">{c.customer_phone}</div>
                      </div>
                      <span className={`text-[9px] uppercase font-semibold px-1.5 rounded ${
                        c.status === "human"   ? "bg-[#DDE7F1] text-[#1F3A57]" :
                        c.status === "pending" ? "bg-[#FDF6E4] text-[#5A4600]" :
                                                 "bg-[#F0EFEA] text-[#5C5A55]"
                      }`}>{c.status}</span>
                    </label>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-[#E5E2D9] flex items-center justify-between gap-3">
          <div className="text-[11px] text-[#8A8780]">
            {selectAll
              ? "All matching conversations will be included."
              : `${selected.size} conversation${selected.size === 1 ? "" : "s"} selected.`}
          </div>
          <button onClick={download}
                  disabled={busy || (!selectAll && selected.size === 0)}
                  data-testid="bulk-export-submit"
                  className="h-10 px-4 rounded-md bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-60 text-white text-sm font-semibold inline-flex items-center gap-1.5">
            <DownloadSimple size={14} weight="fill"/>
            {busy ? "Preparing…" : "Download"}
          </button>
        </div>
      </div>
    </div>
  );
}

